import { ReactElement, useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import { WQAccordion } from '../../../../components/UI/WQAccordion/WQAccordion';
import TextField from '@mui/material/TextField';
import { getLegacyPath } from 'routes/_utils';
import Styles from './AppProcessingForm.module.scss';

import { Body2 } from './../../../../components/UI/MDC/Typography/Typography';
import StyleAuxiliary from '../../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import classes from './AppProcessingForm.module.scss';

/**
 * Wrapping component for App Processing Form
 *
 * @returns {ReactElement} rendered component.
 */

const AppProcessingForm = ({
	app,
	itemID,
	setProcessInfo,
	updateAppByID,
	wqReport,
}) => {
	const [ApprovalHistoryOpen, setApprovalHistoryOpen] = useState(false);
	const [rowLink, setRowLink] = useState('');

	const [application, setApplication] = useState({ ...app });
	const [processAction, setProcessAction] = useState({
		F_Short: 'Select action',
	});

	const prevApplicationRef = useRef();
	const [error, setError] = useState('');
	const [usernameError, setUsernameError] = useState('');

	const onUsernameChangeHandler = e => {
		const sanitizedUsername = e?.target?.value?.replace(/\s+/g, '') ?? '';

		setApplication({
			...application,
			AccountInfo: {
				...application.AccountInfo,
				F_Account_Username: sanitizedUsername,
			},
		});
	};

	const onUsernameKeyDownHandler = e => {
		if (e.key === ' ') {
			e.preventDefault();
		}
	};

	const onPasswordChangeHandler = e => {
		const sanitizedPassword = e?.target?.value?.replace(/\s+/g, '') ?? '';

		setApplication({
			...application,
			AccountInfo: {
				...application.AccountInfo,
				F_Account_Password: sanitizedPassword,
			},
		});
	};

	const onPasswordKeyDownHandler = e => {
		if (e.key === ' ') {
			e.preventDefault();
		}
	};

	useEffect(() => {
		setApplication({ ...application, selectedAction: processAction });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [processAction]);

	// Added check to see if the account username is null and prefilled is not null
	// We then want to set the pre-filled value to our value in the field
	if (
		application?.AccountInfo?.F_Account_Username === null &&
		application?.AccountInfo?.PreFilledUserName !== null
	) {
		setApplication({
			...application,
			AccountInfo: {
				...application.AccountInfo,
				F_Account_Username: application?.AccountInfo?.PreFilledUserName,
			},
		});
	}

	useEffect(() => {
		setRowLink(
			getLegacyPath(`/WorkQueue/ApprovalHistory.aspx?item=${itemID}`),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		// Initially populate the ref with the initial state
		if (prevApplicationRef.current === undefined) {
			prevApplicationRef.current = application;
			return; // exit useEffect
		}

		if (
			JSON.stringify(prevApplicationRef.current) !==
			JSON.stringify(application)
		) {
			// Call your update function
			updateAppByID(application.AppID, application);

			// Update the ref to the new state
			prevApplicationRef.current = application;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [application]);

	useEffect(() => {
		if (
			application?.selectedAction?.F_Short !== 'Select action' &&
			application?.selectedAction?.F_Short !== 'Declined' &&
			(application?.AccountInfo?.F_Account_Username?.trim().length ===
				0 ||
				application?.AccountInfo?.F_Account_Username === null)
		) {
			setUsernameError('*A username must be provided');
		} else {
			setUsernameError('');
		}

		if (
			application?.selectedAction?.F_Short === 'Declined' &&
			(!application?.ApplicationComments ||
				application?.ApplicationComments?.length < 1)
		) {
			setError('*Account Admin Comments required for decline action');
		} else if (
			application?.selectedAction?.F_Short === 'Unable To Process' &&
			(!application?.ApplicationComments ||
				application?.ApplicationComments?.length < 1)
		) {
			setError(
				'*Account Admin Comments required for Unable to Process action',
			);
		} else {
			setError('');
		}
	}, [
		application?.selectedAction?.F_Short,
		application?.ApplicationComments,
		application?.AccountInfo?.F_Account_Username,
	]);

	return (
		<>
			<Modal
				open={ApprovalHistoryOpen}
				onClose={() => setApprovalHistoryOpen(false)}
			>
				<div className={Styles.RequestProcessingModal}>
					<IconButton className={Styles.closeModalIcon}>
						<CloseIcon
							fontSize={'large'}
							onClick={() => {
								setApprovalHistoryOpen(false);
							}}
						/>
					</IconButton>
					<StyleAuxiliary classes={Styles.processingPageControls}>
						<iframe
							title={rowLink}
							src={rowLink}
							// width='100%'
							// height='85%'
						/>
					</StyleAuxiliary>
				</div>
			</Modal>
			<Grid container alignItems='center'>
				<Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
					<WQAccordion
						headerSize={6}
						coloredHeader={true}
						title={`${application?.AppName} (${
							application?.RequestAction
								? application?.RequestAction
								: 'Action N/A'
						})`}
					>
						<Grid
							container
							justifyContent='center'
							className={Styles['status--text--default']}
						>
							{/* STATUS */}
							<Grid item>
								<Body2
									className={
										Styles['body2--margin--override']
									}
								>
									<b>Status: &nbsp;</b>
								</Body2>
							</Grid>

							<Grid item>
								<Body2
									className={
										Styles['body2--margin--override']
									}
								>
									{application?.AccountInfo?.status ?? 'N/A'}
								</Body2>
							</Grid>
						</Grid>

						{application?.Actionable === false && (
							<Grid
								container
								justifyContent='center'
								className={Styles['status--text--default']}
							>
								{/* STATUS */}
								<Grid item>
									<Body2
										className={
											Styles['body2--margin--override']
										}
									>
										<b>Application Not actionable</b>
									</Body2>
								</Grid>
							</Grid>
						)}
						<Chip
							sx={{ p: 2, m: 1, float: 'right' }}
							label='View Approval History'
							onClick={() => setApprovalHistoryOpen(true)}
							clickable
							icon={<VisibilityIcon />}
							variant='outlined'
						/>
						{application?.ExtensionValues?.map(
							(application, key) => (
								<Body2>
									<b>{application?.Description}: </b>
									{application?.Value}
								</Body2>
							),
						)}

						<Grid container alignItems='center'>
							<Grid
								item
								sx={{ p: 1 }}
								xs={6}
								sm={6}
								md={6}
								lg={6}
								xl={6}
							>
								<TextField
									disabled={
										!application.Actionable || wqReport
									}
									className={Styles['user-type__input']}
									label='Username'
									value={
										application?.AccountInfo
											?.F_Account_Username ??
										application?.AccountInfo
											?.PreFilledUserName
									}
									onChange={onUsernameChangeHandler}
									onKeyDown={onUsernameKeyDownHandler}
									name='Username'
									fullWidth
									id='outlined-username-input'
									type='text'
								/>
								<div className={Styles['error--text--default']}>
									<Body2>{usernameError}</Body2>
								</div>
							</Grid>

							{/* password */}
							<Grid container alignItems='center'>
								<Grid
									item
									sx={{ p: 1 }}
									xs={6}
									sm={6}
									md={6}
									lg={6}
									xl={6}
								>
									<TextField
										inputProps={{ maxLength: 15 }}
										disabled={
											!application.Actionable || wqReport
										}
										value={
											application?.AccountInfo
												?.F_Account_Password ??
											application?.AccountInfo
												?.PreFilledPassword
										}
										onChange={onPasswordChangeHandler}
										onKeyDown={onPasswordKeyDownHandler}
										className={Styles['user-type__input']}
										name='Password'
										label='Password'
										fullWidth
										id='outlined-password-input'
										type='text'
									/>
								</Grid>
							</Grid>
							{/* Account Admin Comments -- AKA ApplicationComments*/}
							<Grid container alignItems='center'>
								<Grid
									item
									sx={{ p: 1 }}
									xs={6}
									sm={6}
									md={6}
									lg={6}
									xl={6}
								>
									<TextField
										disabled={
											!application.Actionable || wqReport
										}
										multiline
										className={Styles['user-type__input']}
										value={application?.ApplicationComments}
										onChange={e =>
											setApplication({
												...application,
												ApplicationComments:
													e?.target?.value,
											})
										}
										label='Account Admin Comments'
										inputProps={{
											required: true,
										}}
									/>
								</Grid>
							</Grid>
							<div className={Styles['error--text--default']}>
								<Body2>{error}</Body2>
							</div>
							{/* Request Adjustment Comments */}
							<Grid container alignItems='center'>
								<Grid
									item
									sx={{ p: 1 }}
									xs={6}
									sm={6}
									md={6}
									lg={6}
									xl={6}
								>
									<TextField
										disabled={
											!application.Actionable || wqReport
										}
										multiline
										className={Styles['user-type__input']}
										label='Request Adjustment Comments'
										value={
											application?.RequestAdjustmentComments
										}
										onChange={e =>
											setApplication({
												...application,
												RequestAdjustmentComments:
													e?.target?.value,
											})
										}
									/>
								</Grid>
							</Grid>

							{/* Internal Comments */}
							<Grid container alignItems='center'>
								<Grid
									item
									sx={{ p: 1 }}
									xs={6}
									sm={6}
									md={6}
									lg={6}
									xl={6}
								>
									<TextField
										disabled={
											!application.Actionable || wqReport
										}
										multiline
										className={Styles['user-type__input']}
										label='Internal Comments'
										value={application?.CrossSystemComments}
										onChange={e =>
											setApplication({
												...application,
												CrossSystemComments:
													e?.target?.value,
											})
										}
									/>
								</Grid>
							</Grid>

							{/* Actions */}
							{application.Actionable && (
								<Grid container alignItems='center'>
									<Grid
										item
										sx={{ p: 1 }}
										xs={6}
										sm={6}
										md={6}
										lg={6}
										xl={6}
									>
										<FormControl>
											<InputLabel id='app-processing-action-selector'>
												Process Application
											</InputLabel>
											<Select
												className={
													classes[
														'select--action--default'
													]
												}
												id='app-processing-action__select--wq'
												label='Select Action For Processing'
												value={processAction?.F_Short}
												onChange={e => {
													setProcessAction(
														application?.availableActions?.find(
															app =>
																app.F_Short ===
																e.target.value,
														),
													);
												}}
											>
												{application?.availableActions
													?.map(x => x.F_Short)
													.map(e => (
														<MenuItem value={e}>
															{e}
														</MenuItem>
													))}
											</Select>
										</FormControl>
									</Grid>
								</Grid>
							)}
						</Grid>
					</WQAccordion>
				</Grid>
			</Grid>
		</>
	);
};

export default AppProcessingForm;
